import type { LucideIcon } from "lucide-react";
import { Link, type LinkProps } from "react-router";

import { cx } from "./cx";
import { Spinner } from "./spinner";

/**
 * Buttons (the phone's Button, PillButton and LinkText, apps/mobile/src/components/ui).
 *
 *   primary    filled green: the one main action of a screen area
 *   secondary  white with a green border: the other actions
 *   quiet      green text only: the way off, a small action
 *   danger     filled red: only for the one press that destroys or undoes
 *              (Delete, Cancel sale)
 *
 * 48 px high (the phone's 52 px rule, for a mouse and a finger alike), radius
 * 8, label 16 px weight 600. Loading shows a spinner and refuses clicks, so a
 * slow network never turns one press into two sales.
 */

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";

interface Look {
  variant?: ButtonVariant;
  /** Across the whole width: a page's main action on phone width. */
  fullWidth?: boolean;
  /** 40 px high: for toolbars and the top bar. */
  small?: boolean;
  icon?: LucideIcon;
}

const BASE =
  "inline-flex select-none items-center justify-center gap-2 rounded-control text-center leading-snug font-semibold transition-colors " +
  "disabled:cursor-not-allowed aria-disabled:cursor-not-allowed";

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-on-primary hover:bg-primary-pressed active:bg-primary-pressed " +
    "disabled:bg-disabled disabled:text-muted",
  secondary:
    "border-[1.5px] border-primary bg-surface text-primary hover:bg-primary-soft " +
    "disabled:border-disabled disabled:text-muted disabled:hover:bg-surface",
  quiet: "bg-transparent text-primary hover:bg-primary-soft disabled:text-muted disabled:hover:bg-transparent",
  danger: "bg-danger text-on-primary hover:opacity-90 disabled:bg-disabled disabled:text-muted",
};

function classes({ variant = "primary", fullWidth, small }: Look, className?: string): string {
  return cx(
    BASE,
    VARIANT[variant],
    small ? "min-h-10 px-4 py-1.5 text-label" : "min-h-12 px-6 py-2 text-base",
    fullWidth && "w-full",
    className,
  );
}

type ButtonProps = Look &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
    label: string;
    loading?: boolean;
  };

export function Button({
  label,
  loading,
  disabled,
  variant,
  fullWidth,
  small,
  icon: Icon,
  className,
  type = "button",
  onClick,
  ...rest
}: ButtonProps) {
  const inert = disabled || loading;
  return (
    <button
      type={type}
      disabled={inert}
      aria-busy={loading || undefined}
      onClick={inert ? undefined : onClick}
      className={classes({ variant, fullWidth, small }, className)}
      {...rest}
    >
      {loading ? (
        <Spinner size={small ? 16 : 20} />
      ) : (
        <>
          {Icon && <Icon size={20} aria-hidden="true" />}
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

/**
 * Looks like a button, goes somewhere: "+ Add sale", "View all". A real link,
 * so it opens in a new tab with a middle click and reads as a link.
 */
export function ButtonLink({
  label,
  variant,
  fullWidth,
  small,
  icon: Icon,
  className,
  ...link
}: Look & LinkProps & { label: string }) {
  return (
    <Link className={classes({ variant, fullWidth, small }, className)} {...link}>
      {Icon && <Icon size={20} aria-hidden="true" />}
      <span>{label}</span>
    </Link>
  );
}

/** 40×40 with a 20 px icon. The label is required: it is all a screen reader has. */
export function IconButton({
  icon: Icon,
  label,
  className,
  type = "button",
  ...rest
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & { icon: LucideIcon; label: string }) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        "inline-flex size-10 shrink-0 items-center justify-center rounded-full text-body transition-colors",
        "hover:bg-primary-soft hover:text-primary disabled:cursor-not-allowed disabled:text-disabled",
        className,
      )}
      {...rest}
    >
      <Icon size={20} aria-hidden="true" />
    </button>
  );
}
